import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import { resolveLatestProcessName, getProcess } from '../../transactions/transaction';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

const CANCEL_PARTIAL_REFUND = 'transition/cancel-partial-refund';
const RESOLVE_PARTIAL_REFUND = 'transition/resolve-partial-refund';

function toCents(value) {
  if (!value) return 0;

  const normalized = value.trim();

  if (!normalized || normalized === '0' || normalized === '0.00') {
    return 0;
  }

  const amount = Number(normalized);

  if (isNaN(amount)) {
    return 0;
  }

  return Math.round(amount * 100);
}

/**
 * For partial refund (cancel-partial-refund, resolve-partial-refund), Flex zeroes out
 * payinTotal via calculate-full-refund. We read from metadata to show the correct amount:
 * - Customer: amount they received back (refundResult.amount), or refundable amount if not yet set.
 * - Provider amount is shown via payoutTotal / payout record.
 */
const getPartialRefundTotal = (transaction, isProvider) => {
  const metadata = transaction?.attributes?.metadata || {};
  const currency = transaction?.attributes?.payinTotal?.currency || 'CHF';

  const { originalPayinTotal, originalCustomerCommission, refundResult } = metadata;

  if (isProvider) {
    // Provider: only use metadata when refundResult has been written (e.g. by cron)
    if (refundResult?.totalForProvider != null) {
      return new Money(Math.max(toCents(refundResult.totalForProvider), 0), currency);
    }
    return null;
  }

  // Customer: amount refunded (refundResult.amount) or fallback before refundResult is set
  if (refundResult?.amount != null) {
    return new Money(Math.max(refundResult.amount, 0), currency);
  }
  if (originalPayinTotal != null) {
    const refundable = originalPayinTotal - (originalCustomerCommission || 0);
    return new Money(Math.max(refundable, 0), currency);
  }
  return null;
};

/**
 * A component that renders the total price as a line item.
 *
 * @component
 * @param {Object} props
 * @param {propTypes.transaction} props.transaction - The transaction to render
 * @param {boolean} props.isProvider - Whether the provider is the one receiving the commission
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemTotalPrice = props => {
  const { transaction, isProvider, intl } = props;
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  if (!processName) {
    return null;
  }
  const process = getProcess(processName);
  const lastTransition = transaction?.attributes?.lastTransition;
  const isCompleted = process.isCompleted(lastTransition);
  const isRefunded = process.isRefunded(lastTransition);
  const isPartialRefund = [CANCEL_PARTIAL_REFUND].includes(lastTransition);
  const isPartialRefundAfterDisput = [RESOLVE_PARTIAL_REFUND].includes(lastTransition);

  let providerTotalMessageId = 'OrderBreakdown.providerTotalDefault';
  if (isCompleted) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalReceived';
  } else if (isRefunded || isPartialRefund) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalRefunded';
  }

  const refundResult = transaction?.attributes?.metadata?.refundResult;
  const showRefundProcessingNote = !isProvider && isPartialRefund && !refundResult;
  const totalLabel = isProvider ? (
    <FormattedMessage id={providerTotalMessageId} />
  ) : isPartialRefundAfterDisput && refundResult?.refundToCustomer != null ? (
    <FormattedMessage
      id="OrderBreakdown.refundWithPercentage"
      values={{ percentage: refundResult.refundToCustomer }}
    />
  ) : showRefundProcessingNote ? (
    <>
      <FormattedMessage id="OrderBreakdown.totalRefund" /> **
    </>
  ) : (
    <FormattedMessage
      id={isPartialRefund ? 'OrderBreakdown.totalRefund' : 'OrderBreakdown.total'}
    />
  );

  let totalPrice;
  if ((isPartialRefund || isPartialRefundAfterDisput) && refundResult && !isProvider) {
    // Show the actual refund amount from metadata (originalPayin - customerCommission)
    totalPrice = getPartialRefundTotal(transaction) || transaction.attributes.payinTotal;
  } else if (isPartialRefundAfterDisput && isProvider) {
    totalPrice =
      getPartialRefundTotal(transaction, isProvider) || transaction.attributes.payoutTotal;
  } else {
    totalPrice = isProvider
      ? transaction.attributes.payoutTotal
      : transaction.attributes.payinTotal;
  }

  const formattedTotalPrice = formatMoney(intl, totalPrice);

  return (
    <>
      <hr className={css.totalDivider} />
      <div className={css.lineItemTotal}>
        <div className={css.totalLabel}>{totalLabel}</div>
        <div className={css.totalPrice}>{formattedTotalPrice}</div>
      </div>
    </>
  );
};

export default LineItemTotalPrice;
